import { Checkbox, CheckboxProps, FormControlLabel, styled, SxProps, Theme, useTheme } from '@mui/material';
import React, { AllHTMLAttributes, ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-pro';
import { useVigoTranslation } from 'src/@core/hooks/useVigoTranslation';

interface VigoCheckBoxProps {
  name: string;
  value?: boolean;
  onChange?: ((event: ChangeEvent<HTMLInputElement>, checked: boolean) => void) | undefined;
  sx?: SxProps<Theme>;
  labelSx?: SxProps<Theme>;
  required?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  label?: string;
  labelPlacement?: 'top' | 'bottom' | 'end' | 'start' | undefined;
  useDataGridContext?: boolean;
  dataGridContextParams?: GridRenderEditCellParams<any, any, any>;
  disableLabelTranslation?: boolean;
  inputProps?: AllHTMLAttributes<HTMLInputElement>;
}

const ModeCheckBox = styled((props: CheckboxProps) => (
  <Checkbox focusVisibleClassName=".Mui-focusVisible" {...props} />
))(({ theme }) => ({
  '&.Mui-checked': {
    color: theme.palette.customColors.primaryGradient
  }
}));

export const VigoCheckBox = ({
  name,
  value,
  onChange,
  sx,
  labelSx = {},
  required,
  disabled,
  icon,
  label,
  labelPlacement,
  useDataGridContext,
  dataGridContextParams,
  disableLabelTranslation = false,
  inputProps = {}
}: VigoCheckBoxProps) => {
  // State
  const [checked, setChecked] = useState<boolean | undefined>(value);

  // Hooks
  const { t } = useVigoTranslation();
  let dataGridApiRef: any;
  if (useDataGridContext) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    dataGridApiRef = useGridApiContext();
  }
  const theme = useTheme();

  const handleChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (useDataGridContext && dataGridContextParams) {
      const field = dataGridContextParams.field;
      const id = dataGridContextParams.id;

      dataGridApiRef.current.setEditCellValue({ id, field, value: checked });
    }

    onChange?.(event, checked);
  };

  useEffect(() => {
    setChecked(value);
  }, [value]);

  return (
    <FormControlLabel
      control={
        <ModeCheckBox
          name={name}
          checked={checked}
          onChange={handleChange}
          sx={{
            ...(theme.palette.mode === 'dark' && {
              '&.Mui-checked': {
                color: theme.palette.customColors.primaryGradient
              }
            }),
            ...sx
          }}
          required={required}
          disabled={disabled}
          icon={icon}
          inputProps={{ ...inputProps }}
        />
      }
      label={
        disableLabelTranslation ? label?.replace(/([A-Z])/g, '$1').trim() : t(label?.replace(/([A-Z])/g, '$1').trim())
      }
      labelPlacement={labelPlacement}
      sx={{ ...labelSx }}
    />
  );
};
